import { faBold, faItalic, type IconDefinition } from "@fortawesome/pro-solid-svg-icons";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { mergeRegister } from "@lexical/utils";
import type { TextFormatType } from "lexical";
import { FORMAT_TEXT_COMMAND, $getSelection, $isRangeSelection } from "lexical";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { l } from "@aviary/locales/i18n";

import { ToolbarButton } from "../ToolbarButton";

const InlineFormatButtons = () => {
  const { t } = useTranslation();

  const [editor] = useLexicalComposerContext();

  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);

  const INLINE_FORMATS: {
    icon: IconDefinition;
    style: TextFormatType;
    aria: string;
    isActive: boolean;
  }[] = [
    { icon: faBold, style: "bold", aria: l.aviary.richTextEditor.bold, isActive: isBold },
    { icon: faItalic, style: "italic", aria: l.aviary.richTextEditor.italic, isActive: isItalic },
  ];

  // ensures toolbar buttons are in an active state when they should be
  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat(INLINE_FORMATS[0].style));
      setIsItalic(selection.hasFormat(INLINE_FORMATS[1].style));
    }
  }, [editor]);

  // looks/reads the current state of the editor, and calls updateToolbar when the RTE has edits
  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      })
    );
  }, [updateToolbar, editor]);

  return (
    <>
      {INLINE_FORMATS.map(({ icon, style, aria, isActive }) => (
        <ToolbarButton
          key={style}
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, style);
          }}
          icon={icon}
          aria-label={t(aria)}
          isActive={isActive}
        />
      ))}
    </>
  );
};

export { InlineFormatButtons };
